import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { MaskLine, EASE } from "./Reveal";
import { scrollToId } from "@/lib/scroll";

const Fade = ({ delay, children, className = "" }) => (
  <motion.div className={className} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay, ease: EASE }}>
    {children}
  </motion.div>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(my, { stiffness: 60, damping: 20 });
  const ry = useSpring(mx, { stiffness: 60, damping: 20 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width - 0.5) * 2.4);
    my.set(-((e.clientY - r.top) / r.height - 0.5) * 1.6);
  };

  return (
    <section ref={ref} id="inicio" data-testid="hero-section" onMouseMove={onMove} className="grain relative h-[100svh] min-h-[640px] overflow-hidden bg-[#1E2228] [perspective:1400px]">
      <motion.div style={{ y, rotateX: rx, rotateY: ry }} className="absolute inset-[-4%]">
        <motion.img
          src="/img/real-local-1600.webp"
          srcSet="/img/real-local-800.webp 800w, /img/real-local-1600.webp 1200w"
          sizes="100vw"
          alt="Local comercial realizado por ASSE DECOR con hornacinas de madera retroiluminadas y expositores modulares"
          className="h-full w-full object-cover object-[50%_45%]"
          initial={{ scale: 1.25, clipPath: "inset(18% 22% 18% 22%)" }}
          animate={{ scale: 1.04, clipPath: "inset(0% 0% 0% 0%)" }}
          transition={{ duration: 1.8, ease: EASE }}
          fetchpriority="high"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#1E2228] via-[#1E2228]/55 to-[#1E2228]/30" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#1E2228]/70 to-transparent" />

      <motion.div style={{ y: textY, opacity: fade }} className="container-x relative flex h-full flex-col justify-end pb-14 sm:pb-20">
        <Fade delay={0.9} className="mb-6 flex items-center gap-4">
          <span className="hidden h-px w-12 bg-[#C9A66B] sm:block" />
          <span className="eyebrow text-[#E5D3B3]">Interiorismo · Reformas · Construcción</span>
        </Fade>
        <h1 className="max-w-5xl font-display text-[2.6rem] font-light uppercase leading-[1.02] tracking-[-0.01em] text-white sm:text-6xl lg:text-7xl xl:text-[5.6rem]">
          <MaskLine delay={1.0}>Espacios que</MaskLine>
          <MaskLine delay={1.12}>se diseñan y se</MaskLine>
          <MaskLine delay={1.24}>
            construyen <span className="font-serif-it normal-case text-[#C9A66B]">con detalle</span>
          </MaskLine>
        </h1>
        <div className="mt-8 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <Fade delay={1.5} className="max-w-xl">
            <p className="text-base leading-relaxed text-white/85 sm:text-lg">
              Interiorismo, reformas integrales y construcción con atención personalizada en cada fase: desde la primera idea hasta la entrega final.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <button data-testid="hero-quote-button" onClick={() => scrollToId("contacto")} className="btn-gold group">
                Solicitar presupuesto <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <button data-testid="hero-services-button" onClick={() => scrollToId("servicios")} className="btn-ghost-light">
                Consultar nuestros servicios
              </button>
            </div>
          </Fade>
          <Fade delay={1.8} className="hidden items-center gap-3 lg:flex">
            <button data-testid="hero-scroll-indicator" onClick={() => scrollToId("servicios")} aria-label="Bajar a servicios" className="grid h-14 w-14 place-items-center rounded-full border border-white/30 text-white transition-colors duration-300 hover:bg-white hover:text-[#1E2228]">
              <ArrowDown className="h-4 w-4" />
            </button>
            <span className="eyebrow text-white/70">Desplázate</span>
          </Fade>
        </div>
      </motion.div>
    </section>
  );
}
